// mydatafeed/datafeed.js
// import axios from "axios";
import { API_URL, simulationStart } from "../api-config.js";
import { saveToCache, loadFromCache } from "../functions/datafeed.js";
import { ensureRealtimeSocket, closeRealtimeIfIdle } from "./services/realtime.js";
import { setRealtimeCallback as setRT } from "./websocket.js";
// import { fetchInitialBars, fetchTodayBars, fetchTimescaleMarks } from "./services/api.js";
import { fetchInitialBars, fetchTodayBars } from "./services/api.js";
import { normalizeBars, toMs, toSec, startOfDay } from "./core/time.js";
import { endOfHistory, inflight, subscribers, lastSentBars } from "./core/state.js";

const configurationData = {
  supportedResolutions: ["1", "5", "30", "1H", "2H"],
  supports_timescale_marks: true,
};

const keySR = (s, r) => `${s}:${r}`;

const Datafeed = {
  // TRUE -> modo normal (realtime/API) ; FALSE -> histórico fijo (solo memoria/caché)
  updateBars: true,
  // buffer histórico primario (cuando se usa replaceExternalBars)
  _overrideBars: null,
  _lastBar: null,

  onReady: (cb) => {
    console.log("=====onReady running. configurationData=", configurationData);
    setTimeout(() => cb(configurationData), 0);
  },

  resolveSymbol: (_symbolName, onSymbolResolved) => {
    console.log("======resolveSymbol running. updateBars=", Datafeed.updateBars);
    const symbol = {
      name: "USDCLP",
      description: "USD/CLP by QRT Solutions",
      type: "forex",
      session: "0700-2000",
      timezone: "America/Santiago",
      exchange: "",
      minmov: 1,
      pricescale: 100,
      has_intraday: true,
      visible_plots_set: "ohlcv",
      has_weekly_and_monthly: false,
      supported_resolutions: configurationData.supportedResolutions,
      data_status: Datafeed.updateBars ? "streaming" : "end",
    };
    setTimeout(() => onSymbolResolved(symbol), 0);
  },

  subscribeBars: (_symbolInfo, _resolution, onRealtimeCallback) => {
    // En histórico fijo no abrimos WS ni registramos callbacks
    if (!Datafeed.updateBars) {
      console.log("[subscribeBars] histórico fijo: no websocket");
      return;
    }
    ensureRealtimeSocket(simulationStart);
    setRT(onRealtimeCallback);
  },

  unsubscribeBars: (subscriberUID) => {
    if (subscribers[subscriberUID]) {
      clearInterval(subscribers[subscriberUID]);
      delete subscribers[subscriberUID];
    }
    closeRealtimeIfIdle(Object.keys(subscribers).length === 0);
  },

  replaceExternalBars(minuteCandles) {
    const bars = normalizeBars(minuteCandles);
    saveToCache(bars);
    Datafeed._overrideBars = bars;
    Datafeed._lastBar = bars.length ? bars[bars.length - 1] : null;
    Datafeed.updateBars = false;
    console.log(`[Datafeed] replaceExternalBars -> ${bars.length} bars (histórico fijo ON)`);
    if (bars.length) {
      console.log("[override] head=", bars[0], "tail=", bars[bars.length - 1]);
    }
  },

  enableRealtime() {
    Datafeed.updateBars = true;
    Datafeed._overrideBars = null;
    console.log("[Datafeed] Realtime ON");
  },

  async getBars(symbolInfo, resolution, periodParams, onHistory, onError) {
    const { from, to, firstDataRequest } = periodParams || {};
    const sym = symbolInfo.ticker || symbolInfo.name || "USDCLP";
    const histKey = keySR(sym, resolution);
    const sig = `${histKey}:${from}:${to}`;

    if (inflight.has(sig)) return;
    inflight.add(sig);

    try {
      console.log("[getBars] start", {
        sym, resolution, from, to, firstDataRequest, updateBars: Datafeed.updateBars
      });

      let dataInit;

      if (!Datafeed.updateBars) {
        // HISTÓRICO: usar override primario; si no hay, usar caché
        const cached = loadFromCache() || [];
        const source = Array.isArray(Datafeed._overrideBars) ? Datafeed._overrideBars : cached;
        console.log("[getBars] HISTÓRICO: usando overrideBars=", !!Datafeed._overrideBars, "len=", source.length);
        dataInit = source;
      } else {
        // MODO NORMAL: fetch inicial + actualización del día
        const cached = loadFromCache() || [];
        const token = localStorage.getItem("token");
        const day0 = startOfDay(new Date());

        if (cached.length === 0) {
          console.log("No cached bars, fetching from API...");
          dataInit = await fetchInitialBars({ API_URL, token });
          saveToCache(dataInit);
        } else {
          const today = await fetchTodayBars({ API_URL, token, fromISO: day0.toISOString() });
          dataInit = [...today, ...cached].sort((a, b) => a.time - b.time);
          saveToCache(dataInit);
        }
      }

      console.log("[getBars] dataInit.len", dataInit?.length || 0);

      if (!dataInit || dataInit.length < 1) {
        onHistory([], { noData: true });
        return;
      }

      const oldestMs = dataInit.reduce((min, b) => Math.min(min, b.time), Infinity);
      const reqToMsExclusive = toMs(to || 0) - 1;

      if (!firstDataRequest && reqToMsExclusive <= oldestMs) {
        endOfHistory.set(histKey, true);
        onHistory([], { noData: true });
        return;
      }
      if (!firstDataRequest && endOfHistory.get(histKey)) {
        onHistory([], { noData: true });
        return;
      }

      const nextTime = toSec(oldestMs) - 1;
      console.log("[getBars] onHistoryCallback sending", dataInit.length, "bars, nextTime=", nextTime);
      onHistory(dataInit, { noData: false, nextTime });

      const subscriberUID = `${symbolInfo.ticker}_#_${resolution}`;
      if (dataInit.length > 0) {
        lastSentBars[subscriberUID] = dataInit[dataInit.length - 1];
      }
    } catch (err) {
      onError(err);
    } finally {
      inflight.delete(sig);
    }
  },

  // async getTimescaleMarks(symbolInfo, from, to, onData) {
  //   try {
  //     const sym = (symbolInfo?.ticker || symbolInfo?.name || "USDCLP");
  //     const currency = sym.slice(0, 3); // 'USDCLP' -> 'USD'
  //     const data = await fetchTimescaleMarks({ API_URL, from, to, currency, axios });
  //     onData(Array.isArray(data) ? data : []);
  //   } catch (e) {
  //     console.error("[getTimescaleMarks] error:", e);
  //     onData([]);
  //   }
  // },
};

export default Datafeed;
